import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AgencyState, ManagedAccount, AddClientPayload, DetachClientPayload, ActAsPayload, InviteClientPayload, CreateClientPayload, RenameClientPayload } from "./types";

export const initialState: AgencyState = {
  clients: null,
  loading: false,
  loadError: null,
  addLoading: false,
  addError: null,
  detachLoading: false,
  detachError: null,
  enableLoading: false,
  enableError: null,
  inviteLoading: false,
  inviteError: null,
  createLoading: false,
  createError: null,
  renameLoading: false,
  renameError: null,
};

type LoadingKey = "loading" | "addLoading" | "detachLoading" | "enableLoading" | "inviteLoading" | "createLoading" | "renameLoading";
type ErrorKey = "loadError" | "addError" | "detachError" | "enableError" | "inviteError" | "createError" | "renameError";

function startOp(state: AgencyState, lk: LoadingKey, ek: ErrorKey) {
  state[lk] = true;
  state[ek] = null;
}

function endOp(state: AgencyState, lk: LoadingKey, ek: ErrorKey, error?: string | null) {
  state[lk] = false;
  state[ek] = error ?? null;
}

// === Saga trigger actions ===
export const fetchClients = createAction("agency/fetchClients");
export const addClient = createAction<AddClientPayload>("agency/addClient");
export const detachClient = createAction<DetachClientPayload>("agency/detachClient");
export const actAsClient = createAction<ActAsPayload>("agency/actAsClient");
export const returnToAgency = createAction("agency/returnToAgency");
export const enableAgency = createAction("agency/enableAgency");
export const inviteClient = createAction<InviteClientPayload>("agency/inviteClient");
export const createClient = createAction<CreateClientPayload>("agency/createClient");
export const renameClient = createAction<RenameClientPayload>("agency/renameClient");

const agencySlice = createSlice({
  name: "agency",
  initialState,
  reducers: {
    setAgencyLoading: (state) => {
      startOp(state, "loading", "loadError");
    },
    fetchClientsSuccess: (state, action: PayloadAction<ManagedAccount[]>) => {
      state.loading = false;
      state.clients = action.payload;
    },
    fetchClientsFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "loading", "loadError", action.payload);
      if (state.clients === null) {
        state.clients = [];
      }
    },
    setAddLoading: (state) => {
      startOp(state, "addLoading", "addError");
    },
    addClientSuccess: (state) => {
      endOp(state, "addLoading", "addError");
    },
    addClientFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "addLoading", "addError", action.payload || "Failed to add client");
    },
    setDetachLoading: (state) => {
      startOp(state, "detachLoading", "detachError");
    },
    detachClientSuccess: (state, action: PayloadAction<ManagedAccount>) => {
      endOp(state, "detachLoading", "detachError");
      if (state.clients) {
        const idx = state.clients.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          state.clients[idx] = action.payload;
        }
      }
    },
    detachClientFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "detachLoading", "detachError", action.payload || "Failed to detach client");
    },
    setEnableAgencyLoading: (state) => {
      startOp(state, "enableLoading", "enableError");
    },
    enableAgencySuccess: (state) => {
      endOp(state, "enableLoading", "enableError");
    },
    enableAgencyFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "enableLoading", "enableError", action.payload || "Failed to enable agency mode");
    },
    setInviteLoading: (state) => {
      startOp(state, "inviteLoading", "inviteError");
    },
    inviteClientSuccess: (state) => {
      endOp(state, "inviteLoading", "inviteError");
    },
    inviteClientFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "inviteLoading", "inviteError", action.payload || "Failed to send invitation");
    },
    setCreateLoading: (state) => {
      startOp(state, "createLoading", "createError");
    },
    createClientSuccess: (state) => {
      endOp(state, "createLoading", "createError");
    },
    createClientFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "createLoading", "createError", action.payload || "Failed to create client");
    },
    setRenameLoading: (state) => {
      startOp(state, "renameLoading", "renameError");
    },
    renameClientSuccess: (state, action: PayloadAction<ManagedAccount>) => {
      endOp(state, "renameLoading", "renameError");
      if (state.clients) {
        const idx = state.clients.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          const existingBrands = state.clients[idx].brands;
          state.clients[idx] = {
            ...action.payload,
            brands: action.payload.brands ?? existingBrands,
          };
        }
      }
    },
    renameClientFailure: (state, action: PayloadAction<string | undefined>) => {
      endOp(state, "renameLoading", "renameError", action.payload || "Failed to rename client");
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchClients, (state) => {
      startOp(state, "loading", "loadError");
    });
  },
});

// === Slice actions ===
export const {
  setAgencyLoading,
  fetchClientsSuccess,
  fetchClientsFailure,
  setAddLoading,
  addClientSuccess,
  addClientFailure,
  setDetachLoading,
  detachClientSuccess,
  detachClientFailure,
  setEnableAgencyLoading,
  enableAgencySuccess,
  enableAgencyFailure,
  setInviteLoading,
  inviteClientSuccess,
  inviteClientFailure,
  setCreateLoading,
  createClientSuccess,
  createClientFailure,
  setRenameLoading,
  renameClientSuccess,
  renameClientFailure,
} = agencySlice.actions;

export default agencySlice.reducer;
